import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { chatService, UserInfo } from '../services/chatService';
import { notifyWebViewPageMounted } from '../services/webviewService';
import { initializeEventTracking } from '../services/eventService';

interface AuthContextType {
  userInfo: UserInfo | null;
  loading: boolean;
  error: string | null;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkAuth = async () => {
    try {
      setLoading(true);
      setError(null);
      const info = await chatService.getUserInfo();
      setUserInfo(info);
      initializeEventTracking(info.is_authenticated ? info.user_id : 0);
    } catch (err) {
      console.error('Auth check failed:', err);
      setError('Failed to check authentication');
      setUserInfo({ user_id: 0, is_authenticated: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (!loading) {
      // Notify Android WebView that the page is ready
      notifyWebViewPageMounted();
    }
  }, [loading]);


  return (
    <AuthContext.Provider value={{ userInfo, loading, error, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
